// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import React from 'react';
import { Box, Button, Stack, Alert } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';

import CustomTextField from '@/components/forms/theme-elements/CustomTextField';
import CustomFormLabel from '@/components/forms/theme-elements/CustomFormLabel';
import { apiService } from '@/services/api';

type VerificationFormValues = {
  code1: string;
  code2: string;
  code3: string;
  code4: string;
  code5: string;
  code6: string;
  newPassword?: string;
};

const AuthTwoSteps = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isRecovery = searchParams.get('recovery') === 'true';
  const email = searchParams.get('email') || '';
  const [submitError, setSubmitError] = React.useState<string | null>(null);
  const [submitSuccess, setSubmitSuccess] = React.useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  
  // Refs for each input field
  const inputRefs = React.useRef<(HTMLInputElement | null)[]>([]);

  // Handle input change and auto-focus to next field
  const handleInputChange = (index: number, value: string) => {
    // Only allow single character
    if (value.length > 1) {
      value = value.slice(-1);
    }
    // This will be handled by the register function
    
    // Move to next field if current field has a value
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle key down for backspace navigation
  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !inputRefs.current[index]?.value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const schema = yup.object({
    code1: yup.string().required('Required').length(1, 'Must be 1 digit'),
    code2: yup.string().required('Required').length(1, 'Must be 1 digit'),
    code3: yup.string().required('Required').length(1, 'Must be 1 digit'),
    code4: yup.string().required('Required').length(1, 'Must be 1 digit'),
    code5: yup.string().required('Required').length(1, 'Must be 1 digit'),
    code6: yup.string().required('Required').length(1, 'Must be 1 digit'),
    ...(isRecovery && {
      newPassword: yup
        .string()
        .required('New password is required')
        .min(8, 'Password must be at least 8 characters long'),
    }),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<VerificationFormValues>({
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    resolver: yupResolver(schema),
    mode: 'onSubmit',
    defaultValues: { 
      code1: '', 
      code2: '', 
      code3: '', 
      code4: '', 
      code5: '', 
      code6: '', 
      newPassword: '' ,
    },
  });

  const onSubmit = async (values: VerificationFormValues) => {
    setSubmitError(null);
    setSubmitSuccess(null);
    setIsSubmitting(true);
    
    try {
      // Combine the 6 code fields into a single code string
      const code = `${values.code1}${values.code2}${values.code3}${values.code4}${values.code5}${values.code6}`;
      
      if (isRecovery) {
        // Password recovery verification
        await apiService.verifyPasswordRecovery({
          email,
          code,
          newPassword: values.newPassword!,
        });
        // Navigate to login after successful password change
        navigate('/?passwordReset=true', { replace: true });
      } else {
        // Regular email verification
        await apiService.verifyCustomerEmail({
          email,
          code,
        });
        // Show success message and redirect
        setSubmitSuccess('You can log in');
        setTimeout(() => {
          navigate('/?emailVerified=true', { replace: true });
        }, 2000);
      }
    } catch (err: any) {
      console.error('Verification error:', err);
      setSubmitError(err?.message || 'Verification failed, try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    <Box component="form" onSubmit={handleSubmit(onSubmit)} mt={4}>
      <Stack mb={3}>
        <CustomFormLabel htmlFor="code">
          {isRecovery ? 'Enter verification code' : 'Type your 6 digits security code'}
        </CustomFormLabel>
        <Stack spacing={2} direction="row">
          <CustomTextField
            id="code1"
            variant="outlined"
            fullWidth
            error={!!errors.code1}
            inputRef={(el: any) => (inputRefs.current[0] = el)}
            {...register('code1', {
              onChange: (e) => handleInputChange(0, e.target.value),
            })}
            onKeyDown={(e: any) => handleKeyDown(0, e)}
            inputProps={{ maxLength: 1 }}
          />
          <CustomTextField
            id="code2"
            variant="outlined"
            fullWidth
            error={!!errors.code2}
            inputRef={(el: any) => (inputRefs.current[1] = el)}
            {...register('code2', {
              onChange: (e) => handleInputChange(1, e.target.value),
            })}
            onKeyDown={(e: any) => handleKeyDown(1, e)}
            inputProps={{ maxLength: 1 }}
          />
          <CustomTextField
            id="code3"
            variant="outlined"
            fullWidth
            error={!!errors.code3}
            inputRef={(el: any) => (inputRefs.current[2] = el)}
            {...register('code3', {
              onChange: (e) => handleInputChange(2, e.target.value),
            })}
            onKeyDown={(e: any) => handleKeyDown(2, e)}
            inputProps={{ maxLength: 1 }}
          />
          <CustomTextField
            id="code4"
            variant="outlined"
            fullWidth
            error={!!errors.code4}
            inputRef={(el: any) => (inputRefs.current[3] = el)}
            {...register('code4', {
              onChange: (e) => handleInputChange(3, e.target.value),
            })}
            onKeyDown={(e: any) => handleKeyDown(3, e)}
            inputProps={{ maxLength: 1 }}
          />
          <CustomTextField
            id="code5"
            variant="outlined"
            fullWidth
            error={!!errors.code5}
            inputRef={(el: any) => (inputRefs.current[4] = el)}
            {...register('code5', {
              onChange: (e) => handleInputChange(4, e.target.value),
            })}
            onKeyDown={(e: any) => handleKeyDown(4, e)}
            inputProps={{ maxLength: 1 }}
          />
          <CustomTextField
            id="code6"
            variant="outlined"
            fullWidth
            error={!!errors.code6}
            inputRef={(el: any) => (inputRefs.current[5] = el)}
            {...register('code6', {
              onChange: (e) => handleInputChange(5, e.target.value),
            })}
            onKeyDown={(e: any) => handleKeyDown(5, e)}
            inputProps={{ maxLength: 1 }}
          />
        </Stack>
      </Stack>

      {isRecovery && (
        <Stack mb={3}>
          <CustomFormLabel htmlFor="newPassword">New Password</CustomFormLabel>
          <CustomTextField
            id="newPassword"
            type="password"
            variant="outlined"
            fullWidth
            error={!!errors.newPassword}
            helperText={errors.newPassword?.message}
            {...register('newPassword')}
          />
        </Stack>
      )}

      {submitError && (
        <Alert variant="filled" severity="error" sx={{ mb: 2 }}>
          {submitError}
        </Alert>
      )}

      {submitSuccess && (
        <Alert variant="filled" severity="success" sx={{ mb: 2 }}>
          {submitSuccess}
        </Alert>
      )}

      <Button
        color="primary"
        variant="contained"
        size="large"
        fullWidth
        type="submit"
        disabled={isSubmitting}
      >
        {isSubmitting
          ? 'Processing...'
          : isRecovery
            ? 'Change Password'
            : 'Verify My Account'
        }
      </Button>

      {/*<Stack direction="row" spacing={1} mt={3}>*/}
      {/*  <Typography*/}
      {/*    color="textSecondary"*/}
      {/*    variant="h6"*/}
      {/*    fontWeight="400"*/}
      {/*  >*/}
      {/*    Didn't get the code?*/}
      {/*  </Typography>*/}
      {/*  <Typography*/}
      {/*    component={Link}*/}
      {/*    to="/"*/}
      {/*    fontWeight="500"*/}
      {/*    sx={{*/}
      {/*      textDecoration: 'none',*/}
      {/*      color: 'primary.main',*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    Resend*/}
      {/*  </Typography>*/}
      {/*</Stack>*/}
    </Box>
  );
};

export default AuthTwoSteps;
